// pages/CallbackPage.tsx
import { useEffect, useRef, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Box, CircularProgress, Typography } from "@mui/material";
import jwt_decode from "jwt-decode";
import { getCookie, setCookie } from "../utils/Common";
import { RootContext } from "../context/RootContext";
import { getToken, customerData, orgBucketResponse } from "../services/network";
import { ObjectType } from "../types/tableTypes";
import { delay } from "../utils/utility";
import { Roles } from "../utils/enums";

const testMerchantsId = "517bd3a1-5d5f-4356-bace-5fd28743cf45";

const ROLE_ROUTE_MAP: Record<string, string> = {
  underwriter: "/list-view",
};
const FALLBACK_ROUTE = "/evaluate-loan";

// ✅ Capture URL params immediately at module level
// before any re-renders can clear window.location.search
const urlParams = new URLSearchParams(window.location.search);
const INITIAL_CODE       = urlParams.get("code");
const INITIAL_KC_ERROR   = urlParams.get("error");
const INITIAL_KC_ERR_DESC = urlParams.get("error_description");

export default function CallbackPage() {
  const navigate = useNavigate();
  const hasRun = useRef(false); // ✅ prevents double execution in StrictMode
  const [error, setError] = useState<string | null>(null);

  const {
    dynamicConfig,
    setDecodedTokenData,
    setUserPermissions,
    setUserLoggedIn,
    setDynamicLoader,
    setLoanTypes,
    setOrgData,
    setUserRole,
    setIsAppReady,
  } = useContext(RootContext);

  useEffect(() => {
    // ✅ Wait for config to be fully loaded
    if (!dynamicConfig?.ViteAppClientId || !dynamicConfig?.ViteAppRealms) return;

    // ✅ Prevent double execution
    if (hasRun.current) return;
    hasRun.current = true;

    // ✅ Already authenticated — skip token exchange, just redirect
    if (getCookie("sizer_access_token") && getCookie("orgId")) {
      redirectBasedOnRole();
      return;
    }

    // ✅ Keycloak returned an error
    if (INITIAL_KC_ERROR) {
      setError(INITIAL_KC_ERR_DESC || INITIAL_KC_ERROR);
      return;
    }

    // ✅ No code present
    if (!INITIAL_CODE) {
      setError("No authorization code found. Please try logging in again.");
      return;
    }

    // ✅ All good — exchange code for token
    handleCallback(INITIAL_CODE);

  }, [dynamicConfig]);

  function redirectBasedOnRole() {
    const permissions: string[] = JSON.parse(getCookie("userPermissions") ?? "[]");
    const matchedRoute = permissions.map((role) => ROLE_ROUTE_MAP[role]).find(Boolean);
    navigate(matchedRoute ?? FALLBACK_ROUTE, { replace: true });
  }

  async function handleCallback(code: string) {
    try {
      setCookie("fetchingToken", true);
      setDynamicLoader(true);

      // 1. Exchange code for token
      const headers = { "Content-Type": "application/x-www-form-urlencoded" };
      const body = {
        client_id:    dynamicConfig.ViteAppClientId,
        grant_type:   "authorization_code",
        code,
        // ✅ Must exactly match the redirect_uri registered in Keycloak
        redirect_uri: `${dynamicConfig?.ViteAppHostRedirectUri}/callback`,
      };

      const response = await getToken(body, headers, dynamicConfig);

      if (!response?.data?.access_token) {
        throw new Error("Token exchange failed — no access_token in response.");
      }

      // 2. Decode token
      const token = response.data.access_token;
      const decodedToken: ObjectType = jwt_decode(token);

      // 3. Extract roles
      const roles: string[] = [];
      decodedToken?.realm_access?.roles?.forEach((role: string) => {
        roles.push(role);
      });

      // 4. Store tokens in cookies
      setCookie("sizer_access_token",    token);
      setCookie("sizer_access_expiry",   response.data.expires_in);
      setCookie("sizer_refresh_token",   response.data.refresh_token);
      setCookie("sizer_refresh_expiry",  response.data.refresh_expires_in);
      setCookie("userPermissions",       JSON.stringify(roles));

      // 5. Update context
      setUserPermissions(roles);
      setUserLoggedIn(true);
      setDecodedTokenData(decodedToken);
      if (roles.includes(Roles.loanOfficer)) setUserRole("loanOfficer");

      // 6. Fetch org/customer data — sets orgId cookie
      await loadCustomerData(token, decodedToken);

      await delay(1);

      // 7. ✅ Mark app ready AFTER orgId is set
      setIsAppReady(true);

      // 8. Role-based redirect — /callback never stays in history
      const matchedRoute = roles.map((role) => ROLE_ROUTE_MAP[role]).find(Boolean);
      navigate(matchedRoute ?? FALLBACK_ROUTE, { replace: true });

    } catch (err: any) {
      console.error("🔴 Callback error:", err);
      setError(err.message || "Authentication failed.");
    } finally {
      setCookie("fetchingToken", false);
      setDynamicLoader(false);
    }
  }

  // ✅ Fetches customer data for the logged-in user; falls back to the
  // dynamicConfig partyId (and still sets the partyId cookie) on failure.
  async function fetchCustomerDataOrFallback(decodedToken: any) {
    try {
      const { customerData: fetchCustomerData } = await import("../services/network");
      const resp = await fetchCustomerData(decodedToken.email);
      setCookie("partyId", resp?.data?.partyId ?? dynamicConfig?.partyId);
      return resp?.data;
    } catch {
      setCookie("partyId", dynamicConfig?.partyId);
      return { partyId: dynamicConfig?.partyId };
    }
  }

  // ✅ Resolves the orgId to store based on whether the user is a LAT user
  // and (for LAT users) whether this is a merchants realm.
  function resolveOrgId(isLATUser: any, orgBucketResp: any, decodedToken: any, orgList: any[]) {
    if (!isLATUser) {
      return orgList[0]?.partyId;
    }
    if (dynamicConfig?.ViteAppKcRealm?.includes("merchants")) {
      const env = dynamicConfig?.ViteAppKcRealm?.split("-")?.[1] ?? "prod";
      return decodedToken?.is_test_user ? testMerchantsId : dynamicConfig.clientId;
    }
    return orgBucketResp?.data?.originatorAccountMap?.partyId;
  }

  // ✅ Persists the resolved org/originator data (cookies + localStorage +
  // context) once we know the org has accounts.
  function persistOrgBucketData(orgBucketResp: any, orgList: any[], orgId: any) {
    setCookie("originatorData",        JSON.stringify(orgList?.[0] ?? "{}"));
    localStorage.setItem("originatorData", JSON.stringify(orgList?.[0] ?? "{}"));
    setOrgData(orgList[0]);

    const loanTypes = orgBucketResp?.data?.originatorAccountMap?.loanTypes?.map(
      (lt: any) => lt?.loanTypeName
    );
    setCookie("loanTypes",             JSON.stringify(loanTypes));
    setLoanTypes(loanTypes);
    setCookie("orgId",                 orgId || dynamicConfig.clientId);
    setCookie("originatorAccountMap",  JSON.stringify(orgBucketResp?.data?.originatorAccountMap));
    localStorage.setItem("originatorAccountMap", JSON.stringify(orgBucketResp?.data?.originatorAccountMap));
    setCookie("orgFundingType",        orgBucketResp?.data?.originatorAccountMap?.fundingType ?? "");

    if (orgBucketResp?.data?.originatorAccountMap?.category) {
      setCookie("orgBucketCategory",   orgBucketResp?.data?.originatorAccountMap?.category);
    }
  }

  async function loadCustomerData(token: string, decodedToken: any) {
    try {
      if (dynamicConfig?.ViteAppKcRealm?.includes("merchants")) {
        setCookie("isMerchant", true);
      }

      const newCustomerData: any = await fetchCustomerDataOrFallback(decodedToken);

      const bucketPartyId  = newCustomerData?.partyId ?? dynamicConfig?.partyId;
      const orgBucketResp  = await orgBucketResponse(bucketPartyId);

      if (!orgBucketResp?.data?.accounts?.length) {
        // ✅ Fallback if no accounts — still set orgId so app doesn't hang
        setCookie("orgId", dynamicConfig.clientId);
        return;
      }

      const orgList = orgBucketResp.data.accounts.filter(
        (cObj: any) => cObj.accountType === "ORIGINATOR"
      );
      const isLATUser = orgBucketResp.data.accounts.find(
        (cObj: any) => cObj.accountType === "LAT"
      );

      if (isLATUser) {
        setCookie("latData", JSON.stringify(isLATUser));
        localStorage.setItem("latData", JSON.stringify(isLATUser));
      }

      const orgId = resolveOrgId(isLATUser, orgBucketResp, decodedToken, orgList);
      persistOrgBucketData(orgBucketResp, orgList, orgId);

    } catch (err) {
      console.error("🔴 loadCustomerData error:", err);
      setCookie("orgId",   dynamicConfig?.clientId);
      setCookie("partyId", dynamicConfig?.partyId);
    }
  }

  // ── Error state ────────────────────────────────────────────────────────────
  if (error) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center"
        justifyContent="center" minHeight="100vh" gap={2}>
        <Typography variant="h6" color="error">Authentication Error</Typography>
        <Typography variant="body2" color="text.secondary">{error}</Typography>
      </Box>
    );
  }

  // ── Loading state ──────────────────────────────────────────────────────────
  return (
    <Box display="flex" flexDirection="column" alignItems="center"
      justifyContent="center" minHeight="100vh" gap={2}>
      <CircularProgress />
      <Typography variant="body1" color="text.secondary">
        Authenticating, please wait...
      </Typography>
    </Box>
  );
}